import React, { useState, useEffect, forwardRef, useRef, useImperativeHandle } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { FiCreditCard, FiCopy, FiCheckCircle, FiInfo, FiLock, FiX } from 'react-icons/fi';
import { FaPix } from 'react-icons/fa6';
import { Colors } from '../../utils/colors';
import { PaymentOptionsStyle } from './PaymentOptions.styles';
import {
  mountSecureFields,
  unmountSecureFields,
  createCardTokenFromFields,
} from '../../services/mercadopagoSdk';


const TermsNotice = () => (
  <p style={{
    fontSize: '11px',
    color: 'rgba(255,255,255,0.4)',
    margin: '10px 0 0',
    lineHeight: '1.5',
    textAlign: 'center',
  }}>
    Ao concluir, você declara que leu e está de acordo com os{' '}
    <a
      href="/termo-de-uso-e-politica-de-privacidade"
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: 'rgba(255,215,0,0.6)', textDecoration: 'underline' }}
    >
      termos de uso e política de privacidade
    </a>.
  </p>
);

const CopyNotification = ({ isVisible }) => (
  isVisible ? (
    <div className="copy-notification">
      <FiCheckCircle className="icon" />
      Código PIX copiado com sucesso!
    </div>
  ) : null
);

export const PaymentOptions = forwardRef(({
  onPixSelect,
  onCardSelect,
  onPaymentConfirm,
  isProcessing = false,
  qrCode = null,
  pixCopiaECola = null,
  paymentTxid = null,
  onCancelPayment = () => {},
  cartTotal = 0,
  pixTotal = null,
  cardTotal = null,
  maxInstallments: maxInstallmentsProp = 12,
}, ref) => {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const innerRef = useRef(null);

  // Expõe scrollIntoView (usado no mobile) e closeModal (fechar o modal do
  // formulário quando o pagamento conclui — senão ele cobre o próximo passo).
  useImperativeHandle(
    ref,
    () => ({
      scrollIntoView: (opts) => innerRef.current?.scrollIntoView(opts),
      closeModal: () => setSelectedMethod(null),
    }),
    [],
  );
  const [pixData, setPixData] = useState({
    name: '',
    email: '',
    cpf: '',
    telefone: '', // WhatsApp (só dígitos)
  });
  const [cardData, setCardData] = useState({
    cardholderName: '',
    email: '',
    cpf: '',
    // número/validade/CVV agora são Secure Fields (iframes do MP) — não ficam
    // no estado. Só nome/email/cpf/telefone/parcelas são nossos.
    installments: 1,
    telefone: '', // WhatsApp (só dígitos)
  });
  const [showCopyNotification, setShowCopyNotification] = useState(false);
  const [cardError, setCardError] = useState('');
  const [tokenizing, setTokenizing] = useState(false);

  // Secure Fields (PCI): monta os iframes do MP quando o cartão é selecionado.
  useEffect(() => {
    if (selectedMethod !== 'credit_card') return undefined;
    let active = true;
    setCardError('');
    mountSecureFields().catch((err) => {
      if (active) {
        setCardError('Não foi possível carregar o formulário seguro de cartão. Recarregue a página.');
      }
      console.error('[SecureFields] falha ao montar:', err?.message);
    });
    return () => {
      active = false;
      unmountSecureFields();
    };
  }, [selectedMethod]);

  const handlePixSelect = () => {
    // Toggle: clicar de novo no método já aberto fecha o formulário.
    const opening = selectedMethod !== 'pix';
    setSelectedMethod(opening ? 'pix' : null);
    if (opening) onPixSelect();
  };

  const handleCardSelect = () => {
    const opening = selectedMethod !== 'credit_card';
    setSelectedMethod(opening ? 'credit_card' : null);
    if (opening) onCardSelect();
  };

  const handlePixPayment = async () => {
    if (!pixReady) return;
    // Passar os dados com a estrutura esperada
    onPaymentConfirm('pix', {
      name: pixData.name,
      email: pixData.email,
      cpf: pixData.cpf,
      telefone: pixData.telefone,
      pixData, // Manter referência ao objeto completo também
    });
  };

  const handleCardPayment = async () => {
    if (!cardReady || tokenizing) return;
    const cleanCPF = cardData.cpf.replace(/\D/g, '');
    setCardError('');
    setTokenizing(true);
    try {
      // Tokeniza pelos SECURE FIELDS: número/validade/CVV estão nos iframes do
      // MP e NUNCA tocam nosso JS. Daqui só vão nome do titular + CPF.
      const tok = await createCardTokenFromFields({
        cardholderName: cardData.cardholderName,
        identificationNumber: cleanCPF,
      });
      onPaymentConfirm('credit_card', {
        ...cardData,
        cpf: cleanCPF,
        cardToken: tok.id,
        firstSixDigits: tok.firstSixDigits,
      });
    } catch (err) {
      setCardError(err?.message || 'Confira os dados do cartão e tente novamente.');
    } finally {
      setTokenizing(false);
    }
  };

  const formatCPF = (value) => {
    if (!value) return '';
    const cleaned = value.replace(/\D/g, '');
    return cleaned
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
      .substring(0, 14);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // Mostrar notificação de sucesso
    setShowCopyNotification(true);
    setTimeout(() => {
      setShowCopyNotification(false);
    }, 3000);
  };
  // handleCancelClick removido: cancelar agora é feito com o botão Voltar do fluxo

  // Valor formatado e prontidão dos formulários (habilita o botão só quando os
  // dados obrigatórios estão preenchidos — sem alert() intrusivo).
  const formatBRL = (cents) =>
    (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const isEmail = (v) => /\S+@\S+\.\S+/.test((v || '').trim());
  const cpfDigits = (v) => (v || '').replace(/\D/g, '');
  const phoneDigits = (v) => (v || '').replace(/\D/g, '');
  const phoneOk = (v) => {
    const d = phoneDigits(v);
    return d.length >= 10 && d.length <= 11;
  };
  // Máscara WhatsApp: (XX) XXXXX-XXXX
  const formatWhats = (v) => {
    const d = phoneDigits(v).slice(0, 11);
    if (d.length <= 2) return d;
    if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  };
  const pixReady =
    Boolean(pixData.name.trim()) &&
    isEmail(pixData.email) &&
    cpfDigits(pixData.cpf).length === 11 &&
    phoneOk(pixData.telefone);
  // Número/validade/CVV são Secure Fields (validados pelo SDK do MP na
  // tokenização), então só checamos nome/email/cpf/telefone aqui.
  const cardReady =
    Boolean(cardData.cardholderName.trim()) &&
    isEmail(cardData.email) &&
    cpfDigits(cardData.cpf).length === 11 &&
    phoneOk(cardData.telefone);

  const payButtonStyle = (enabled) => ({
    flex: 1,
    padding: '15px',
    background: Colors.yellow,
    color: Colors.dark,
    border: 'none',
    borderRadius: '8px',
    fontWeight: 800,
    fontSize: '15px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    cursor: enabled ? 'pointer' : 'not-allowed',
    opacity: enabled ? 1 : 0.5,
    transition: 'opacity 0.2s ease',
  });

  const incompleteHint = (
    <p style={{ fontSize: '12px', color: Colors.gold, textAlign: 'center', margin: '8px 0 0' }}>
      Preencha os dados acima para continuar.
    </p>
  );

  return (
    <PaymentOptionsStyle ref={innerRef}>
      <div className="payment-methods">
        <div
          className={`payment-method ${selectedMethod === 'pix' ? 'active' : ''}`}
          onClick={handlePixSelect}
        >
          <div className="method-badge">Recomendado</div>
          <div className="method-header">
            <FaPix className="icon" />
            <span>PIX</span>
          </div>
          <div className="method-description">
            Pagamento instantâneo via PIX. Rápido e seguro.
          </div>
        </div>

        <div
          className={`payment-method ${selectedMethod === 'credit_card' ? 'active' : ''}`}
          onClick={handleCardSelect}
        >
          <div className="method-header">
            <FiCreditCard className="icon" />
            <span>Cartão de Crédito</span>
          </div>
          <div className="method-description">
            Parcelado em até 12x.
          </div>
        </div>
      </div>

      {selectedMethod && (
        <div
          className="pay-modal-overlay"
          role="dialog"
          aria-modal="true"
        >
          <div className="pay-modal">
            <button
              type="button"
              className="pay-modal-close"
              aria-label="Fechar"
              onClick={() => setSelectedMethod(null)}
            >
              <FiX />
            </button>

      {selectedMethod === 'pix' && (
        <div className="payment-form">
          <h4><FaPix /> Dados para Pagamento PIX</h4>

          <div className="form-group">
            <label>Nome Completo</label>
            <input
              type="text"
              placeholder="Digite seu nome completo"
              value={pixData.name}
              onChange={(e) => setPixData({ ...pixData, name: e.target.value })}
              disabled={isProcessing}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                placeholder="seu.email@example.com"
                value={pixData.email}
                onChange={(e) => setPixData({ ...pixData, email: e.target.value })}
                disabled={isProcessing}
              />
            </div>

            <div className="form-group">
              <label>CPF</label>
              <input
                type="text"
                placeholder="000.000.000-00"
                inputMode="numeric"
                pattern="\d*"
                value={formatCPF(pixData.cpf)}
                onChange={(e) => setPixData({ ...pixData, cpf: e.target.value.replace(/\D/g, '') })}
                disabled={isProcessing}
                maxLength={14}
              />
            </div>

            <div className="form-group">
              <label>WHATSAPP</label>
              <input
                type="tel"
                inputMode="numeric"
                placeholder="(11) 91234-5678"
                value={formatWhats(pixData.telefone)}
                onChange={(e) =>
                  setPixData({ ...pixData, telefone: e.target.value.replace(/\D/g, '').slice(0, 11) })
                }
                disabled={isProcessing}
                maxLength={16}
              />
            </div>
          </div>

          {!qrCode && (
            <>
              <TermsNotice />
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button
                  style={payButtonStyle(!isProcessing && pixReady)}
                  onClick={handlePixPayment}
                  disabled={isProcessing || !pixReady}
                >
                  {isProcessing ? (
                    'Gerando PIX…'
                  ) : (
                    <><FaPix /> Gerar PIX • {formatBRL(cartTotal)}</>
                  )}
                </button>
              </div>
              {!pixReady && !isProcessing && incompleteHint}
            </>
          )}

          {qrCode && pixCopiaECola && (
            <div className="qr-code-container">
              <div className="qr-code-title">Escaneie o QR Code PIX</div>
              <p style={{ color: Colors.gold, fontSize: '12px', margin: '0 0 8px', lineHeight: 1.4 }}>
                Abra o app do seu banco, escaneie o código e confirme o pagamento.
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
                <QRCodeSVG className="qr-svg" value={pixCopiaECola} size={180} level="H" />
              </div>
              <div className="copy-code">
                <div className="code-label">OU COPIE O CÓDIGO PIX:</div>
                <div className="code-box">
                  <input type="text" readOnly value={pixCopiaECola} />
                  <button onClick={() => copyToClipboard(pixCopiaECola)}>
                    <FiCopy style={{ verticalAlign: '-2px', marginRight: 4 }} /> Copiar
                  </button>
                </div>
              </div>
              <p style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                color: Colors.gold, fontSize: '12px', margin: '14px 0 0', lineHeight: 1.4,
              }}>
                <FiInfo style={{ flexShrink: 0 }} />
                Assim que o pagamento for confirmado, seu acesso é liberado automaticamente.
              </p>
            </div>
          )}
        </div>
      )}

      {selectedMethod === 'credit_card' && (
        <div className="payment-form">
          <h4><FiCreditCard /> Dados do Cartão de Crédito</h4>

          <div className="form-group">
            <label>Nome do Titular</label>
            <input
              type="text"
              placeholder="Nome como aparece no cartão"
              value={cardData.cardholderName}
              onChange={(e) =>
                setCardData({ ...cardData, cardholderName: e.target.value })
              }
              disabled={isProcessing}
            />
          </div>

          <div className="form-group">
            <label>E-mail</label>
            <input
              type="email"
              placeholder="seu.email@exemplo.com"
              value={cardData.email}
              onChange={(e) =>
                setCardData({ ...cardData, email: e.target.value })
              }
              disabled={isProcessing}
            />
          </div>

          <div className="form-group">
            <label>CPF</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              placeholder="000.000.000-00"
              maxLength={14}
              value={formatCPF(cardData.cpf)}
              onChange={(e) =>
                setCardData({ ...cardData, cpf: e.target.value.replace(/\D/g, '') })
              }
              disabled={isProcessing}
            />
          </div>

          <div className="form-group">
            <label>WHATSAPP</label>
            <input
              type="tel"
              inputMode="numeric"
              placeholder="(11) 91234-5678"
              maxLength={16}
              value={formatWhats(cardData.telefone)}
              onChange={(e) =>
                setCardData({ ...cardData, telefone: e.target.value.replace(/\D/g, '').slice(0, 11) })
              }
              disabled={isProcessing}
            />
          </div>

          <div className="form-group">
            <label>Número do Cartão</label>
            {/* IFRAME do Mercado Pago (Secure Field) — o número não toca nosso JS. */}
            <div id="mp-ebooks-card-number" className="mp-secure-field" />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Data de Validade</label>
              <div id="mp-ebooks-card-expiry" className="mp-secure-field" />
            </div>

            <div className="form-group">
              <label>CVV</label>
              <div id="mp-ebooks-card-cvv" className="mp-secure-field" />
            </div>
          </div>

          <div className="form-group">
            <label>Parcelamento (Mínimo R$ 5,00 por parcela)</label>
            <select
              value={cardData.installments}
              onChange={(e) => {
                const newInstallments = parseInt(e.target.value);
                // Validar se parcela não fica abaixo de R$ 5 (500 centavos)
                const minInstallmentValue = 500; // R$ 5,00 em centavos
                const valuePerInstallment = cartTotal / newInstallments;
                
                if (valuePerInstallment >= minInstallmentValue) {
                  setCardData({
                    ...cardData,
                    installments: newInstallments,
                  });
                } else {
                  console.warn(`⚠️ Parcela de ${newInstallments}x resulta em R$ ${(valuePerInstallment / 100).toFixed(2)} por parcela (mínimo: R$ 5,00)`);
                  // Não atualizar se não atingir o mínimo
                }
              }}
              disabled={isProcessing}
            >
              {(() => {
                const minInstallmentValue = 500; // R$ 5,00 em centavos
                const maxInstallments = Math.min(
                  maxInstallmentsProp, // limite do produto (tabela)
                  12,
                  Math.floor(cartTotal / minInstallmentValue)
                );
                const allowedInstallments = [1, 2, 3, 4, 6, 8, 10, 12].filter(
                  (n) => n <= maxInstallments
                );

                return allowedInstallments.map((n) => {
                  const valuePerInstallment = cartTotal / n;
                  return (
                    <option key={n} value={n}>
                      {n}x de R$ {(valuePerInstallment / 100).toFixed(2)}
                    </option>
                  );
                });
              })()}
            </select>
            {(() => {
              const minInstallmentValue = 500;
              const maxInstallments = Math.min(
                maxInstallmentsProp,
                Math.floor(cartTotal / minInstallmentValue)
              );
              return maxInstallments < 12 ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: Colors.gold,
                  fontSize: '12px',
                  marginTop: '6px',
                  fontStyle: 'italic'
                }}>
                  <FiInfo style={{ flexShrink: 0 }} /> Máximo {maxInstallments}x com este valor
                </div>
              ) : null;
            })()}
          </div>

          <p style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: Colors.gold, margin: '4px 0 0' }}>
            <FiLock /> Seus dados de cartão são capturados com segurança pelo Mercado Pago.
          </p>
          {cardError && (
            <p style={{ color: '#ff6b6b', fontSize: '13px', margin: '8px 0 0' }} role="alert">
              {cardError}
            </p>
          )}

          <TermsNotice />
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <button
              style={payButtonStyle(!isProcessing && !tokenizing && cardReady)}
              onClick={handleCardPayment}
              disabled={isProcessing || tokenizing || !cardReady}
            >
              {isProcessing || tokenizing ? (
                'Processando…'
              ) : (
                <><FiLock /> Pagar {formatBRL(cartTotal)}</>
              )}
            </button>
          </div>
          {!cardReady && !isProcessing && incompleteHint}
        </div>
      )}
          </div>
        </div>
      )}
      <CopyNotification isVisible={showCopyNotification} />
    </PaymentOptionsStyle>
  );
});

PaymentOptions.displayName = 'PaymentOptions';

export default PaymentOptions;
